import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { cn } from "@/dentlab/src/lib/util";

interface VerticalResizablePanelsProps {
	topContent: React.ReactNode;
	bottomContent: React.ReactNode;
	initialTopHeight?: number | string;
	minTopHeight?: number;
	minBottomHeight?: number;
	fullscreenPanel?: "top" | "bottom" | null;
	onHeightChange?: (heightPercent: number) => void;
	onFullscreenChange?: (panel: "top" | "bottom" | null) => void;
	enableBackgroundDoubleClickFullscreen?: boolean;
	className?: string;
}

const DIVIDER_HEIGHT = 4;

const INTERACTIVE_SELECTORS =
	'input, textarea, select, button, label, a, [role="button"], [role="tab"], [tabindex], [contenteditable], .cursor-pointer';

const isBackgroundClick = (
	e: React.MouseEvent,
	container: HTMLElement
): boolean => {
	let el = e.target as HTMLElement | null;
	while (el && el !== container) {
		if (el.matches(INTERACTIVE_SELECTORS)) return false;
		el = el.parentElement;
	}
	return true;
};

export const VerticalResizablePanels: React.FC<
	VerticalResizablePanelsProps
> = ({
	topContent,
	bottomContent,
	initialTopHeight = "50%",
	minTopHeight = 200,
	minBottomHeight = 200,
	fullscreenPanel = null,
	onHeightChange,
	onFullscreenChange,
	enableBackgroundDoubleClickFullscreen = true,
	className,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const topPanelRef = useRef<HTMLDivElement>(null);
	const bottomPanelRef = useRef<HTMLDivElement>(null);
	const [topHeight, setTopHeight] = useState<number>(0);
	const [containerSize, setContainerSize] = useState(0);
	const [isResizing, setIsResizing] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);
	const previousTopHeightRef = useRef<number | null>(null);
	const initializedRef = useRef(false);

	// Track container size via ResizeObserver (skip during active resize to avoid lag)
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			if (!isResizing) {
				setContainerSize(el.clientHeight);
			}
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, [isResizing]);

	// Initialize top height from percentage or pixel value
	useEffect(() => {
		if (initializedRef.current || containerSize === 0) return;
		initializedRef.current = true;
		if (
			typeof initialTopHeight === "string" &&
			initialTopHeight.endsWith("%")
		) {
			const pct = parseFloat(initialTopHeight) / 100;
			setTopHeight(Math.max(minTopHeight, containerSize * pct));
		} else if (typeof initialTopHeight === "number") {
			setTopHeight(Math.max(minTopHeight, initialTopHeight));
		}
	}, [containerSize, initialTopHeight, minTopHeight]);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (fullscreenPanel) return;
			setIsResizing(true);
			e.preventDefault();
		},
		[fullscreenPanel]
	);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isResizing || !containerRef.current) return;
			const rect = containerRef.current.getBoundingClientRect();
			const newTopHeight = e.clientY - rect.top;
			const newBottomHeight = rect.height - newTopHeight - DIVIDER_HEIGHT;
			if (
				newTopHeight >= minTopHeight &&
				newBottomHeight >= minBottomHeight
			) {
				setTopHeight(newTopHeight);
			}
		},
		[isResizing, minTopHeight, minBottomHeight]
	);

	const handleMouseUp = useCallback(() => {
		setIsResizing(false);
		if (onHeightChange && !fullscreenPanel && containerRef.current) {
			const heightPercent =
				(topHeight / containerRef.current.clientHeight) * 100;
			onHeightChange(heightPercent);
		}
	}, [topHeight, onHeightChange, fullscreenPanel]);

	const toggleFullscreen = useCallback(
		(panel: "top" | "bottom") => {
			if (!onFullscreenChange) return;
			if (fullscreenPanel) {
				onFullscreenChange(null);
			} else {
				previousTopHeightRef.current = topHeight;
				onFullscreenChange(panel);
			}
		},
		[fullscreenPanel, topHeight, onFullscreenChange]
	);

	const handleDividerDoubleClick = useCallback(() => {
		toggleFullscreen("bottom");
	}, [toggleFullscreen]);

	const handleTopPanelDoubleClick = useCallback(
		(e: React.MouseEvent) => {
			if (
				!topPanelRef.current ||
				!isBackgroundClick(e, topPanelRef.current)
			)
				return;
			toggleFullscreen("top");
		},
		[toggleFullscreen]
	);

	const handleBottomPanelDoubleClick = useCallback(
		(e: React.MouseEvent) => {
			if (
				!bottomPanelRef.current ||
				!isBackgroundClick(e, bottomPanelRef.current)
			)
				return;
			toggleFullscreen("bottom");
		},
		[toggleFullscreen]
	);

	// Restore previous height when exiting fullscreen
	useEffect(() => {
		if (!fullscreenPanel && previousTopHeightRef.current !== null) {
			setTopHeight(previousTopHeightRef.current);
			previousTopHeightRef.current = null;
		}
	}, [fullscreenPanel]);

	// Trigger animation flag on fullscreen changes
	useEffect(() => {
		if (containerSize === 0) return;
		setIsAnimating(true);
		const timer = setTimeout(() => setIsAnimating(false), 450);
		return () => clearTimeout(timer);
	}, [fullscreenPanel]);

	useEffect(() => {
		if (isResizing) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
			document.body.style.userSelect = "none";
			document.body.style.cursor = "row-resize";
			return () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
				document.body.style.userSelect = "";
				document.body.style.cursor = "";
			};
		}
	}, [isResizing, handleMouseMove, handleMouseUp]);

	const computedTopHeight =
		fullscreenPanel === "top"
			? containerSize || "100%"
			: fullscreenPanel === "bottom"
				? 40
				: topHeight;

	const useFlexBottom = !fullscreenPanel && !isAnimating;

	const hiddenBottom = fullscreenPanel === "top";
	const hiddenTop = fullscreenPanel === "bottom";
	const transitionClass = !isResizing
		? "transition-[height] duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
		: "";

	const computedBottomHeight = useFlexBottom
		? undefined
		: hiddenBottom
			? "0px"
			: fullscreenPanel === "bottom" && containerSize
				? `${containerSize - 40 - DIVIDER_HEIGHT}px`
				: undefined;

	return (
		<div
			ref={containerRef}
			className={cn(
				"flex flex-col h-full w-full overflow-hidden",
				className
			)}
		>
			{/* Top Panel */}
			<div
				ref={topPanelRef}
				className={cn(
					"bg-white shrink-0",
					hiddenTop ? "overflow-hidden" : "overflow-auto",
					transitionClass,
					isAnimating && "will-change-[height]"
				)}
				style={{
					height:
						typeof computedTopHeight === "number"
							? `${computedTopHeight}px`
							: computedTopHeight,
				}}
				onDoubleClick={
					enableBackgroundDoubleClickFullscreen
						? handleTopPanelDoubleClick
						: undefined
				}
			>
				{topContent}
			</div>

			{/* Resize Handle */}
			<div
				className={cn(
					"relative bg-gray-200 select-none shrink-0",
					hiddenBottom ? "h-0" : "h-1",
					!fullscreenPanel && "cursor-row-resize hover:bg-blue-400",
					"transition-all duration-200",
					isResizing && "bg-blue-500"
				)}
				onMouseDown={handleMouseDown}
				onDoubleClick={handleDividerDoubleClick}
			>
				{!hiddenBottom && (
					<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-1 rounded-full bg-gray-400 opacity-50" />
				)}
			</div>

			{/* Bottom Panel — always mounted, hidden via height:0 + overflow:hidden */}
			<div
				ref={bottomPanelRef}
				className={cn(
					"bg-white",
					useFlexBottom && "flex-1",
					hiddenBottom ? "overflow-hidden" : "overflow-auto",
					transitionClass,
					isAnimating && "will-change-[height]"
				)}
				style={{ height: computedBottomHeight }}
				onDoubleClick={
					enableBackgroundDoubleClickFullscreen
						? handleBottomPanelDoubleClick
						: undefined
				}
			>
				{bottomContent}
			</div>
		</div>
	);
};
